import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const ReserveButton = ({ postId }) => {
  const { userId } = useAuth();
  const [reserved, setReserved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Check if user already reserved
  const checkReservation = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reserve/`, {
        params: { user_id: userId, post_id: postId },
      });
      setReserved(response.data.reserved);
    } catch (error) {
      console.error('Check reservation failed', error);
    } finally {
      setLoading(false);
    }
  };

  // Reserve spot
  const reserveSpot = async () => {
    setConfirmLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/reserve/`, {
        user_id: userId,
        post_id: postId,
      });
      if (response.data.reserved) {
        setReserved(true);
      }
    } catch (error) {
      console.error('Reservation failed', error);
    } finally {
      setConfirmLoading(false);
      setOpenDialog(false);
    }
  };

  // Track loading duration
  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingSeconds(0);
      timer = setInterval(() => setLoadingSeconds((prev) => prev + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (userId && postId) {
      checkReservation();
    }
  }, [userId, postId]);

  return (
    <>
      {/* Initial Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(3px)',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress
              size={60}
              thickness={6}
              sx={{
                color: 'transparent',
                '& .MuiCircularProgress-circle': {
                  stroke: 'url(#better-gradient)',
                },
              }}
            />
            <svg width="0" height="0">
              <defs>
                <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f30b96ff" />
                  <stop offset="50%" stopColor="#221ce0ff" />
                  <stop offset="100%" stopColor="#0eacebff" />
                </linearGradient>
              </defs>
            </svg>
            <Typography
              variant="h6"
              sx={{ mt: 2, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}
            >
              Loading Details... ({loadingSeconds} sec)
            </Typography>
          </Box>
        </Box>
      )}

      {/* Reserve Button */}
      {!loading &&
        (reserved ? (
          <Button
            variant="contained"
            disabled
            sx={{
              fontSize: '1rem',
              padding: '12px 28px',
              borderRadius: '10px',
              backgroundImage: 'linear-gradient(to right, #5dcf1bcb, #1f1f1aff)',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              textTransform: 'none',
              opacity: 0.8,
              cursor: 'not-allowed',
            }}
          >
            Reserved
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            sx={{
              fontSize: '1rem',
              padding: '12px 28px',
              borderRadius: '10px',
              backgroundImage: 'linear-gradient(to right, #1b1ed1ff, #282c30ff)',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              textTransform: 'none',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
                backgroundImage: 'linear-gradient(to right, #2114d3ff, #1b1d1fff)',
              },
            }}
          >
            Reserve Spot
          </Button>
        ))}

      {/* Confirmation Dialog */}
     <Dialog
  open={openDialog}
  onClose={() => !confirmLoading && setOpenDialog(false)}
  PaperProps={{
    sx: {
      borderRadius: 3,
      padding: 2,
      minWidth: 360,
      backgroundColor:'#000000'
    },
  }}
>
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      background: 'linear-gradient(90deg, #1b1ed1, #000000)',
      borderRadius: '8px 8px 0 0',
      padding: 1.5,
      color: '#fff',
    }}
  >
    <Box
      component="span"
      sx={{
        fontSize: 24,
      }}
    >
      ⚡
    </Box>
    <DialogTitle sx={{ fontWeight: 'bold', color: '#fff', m: 0 }}>
      Confirm Reservation
    </DialogTitle>
  </Box>

  <DialogContent
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      paddingY: 3,
    }}
  >
    {confirmLoading && <CircularProgress size={32} />}
    <DialogContentText sx={{ textAlign: 'center', fontSize: 16 }}>
      {confirmLoading
        ? 'Reserving your spot... Please wait.'
        : 'Are you sure you want to reserve this spot?'}
    </DialogContentText>
  </DialogContent>

  {!confirmLoading && (
    <DialogActions sx={{ justifyContent: 'center', paddingBottom: 2 }}>
      <Button
        onClick={() => setOpenDialog(false)}
        variant="outlined"
        color="white"
        sx={{ px: 3, py: 1.2, borderRadius: 2 }}
      >
        Cancel
      </Button>
      <Button
        onClick={reserveSpot}
        variant="contained"
        sx={{
          px: 3,
          py: 1.2,
          borderRadius: 2,
          backgroundImage: 'linear-gradient(to right, #1b1ed1, #282c30)',
          color: '#fff',
          '&:hover': {
            backgroundImage: 'linear-gradient(to right, #282c30, #1b1ed1)',
          },
        }}
      >
        Confirm
      </Button>
    </DialogActions>
  )}
</Dialog>

    </>
  );
};

export default ReserveButton;
